import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const ProcessSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const BackgroundGlow = styled.div`
  position: absolute;
  bottom: -200px;
  left: 50%;
  transform: translateX(-50%);
  width: 800px;
  height: 400px;
  background: ${props => props.theme.gradients.glow};
  filter: blur(120px);
  opacity: 0.3;
  pointer-events: none;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const SectionLabel = styled(motion.span)`
  display: inline-block;
  padding: 8px 16px;
  background: ${props => props.theme.gradients.subtle};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 100px;
  font-size: 0.875rem;
  font-weight: ${props => props.theme.typography.weights.medium};
  color: ${props => props.theme.colors.accentPurple};
  margin-bottom: ${props => props.theme.spacing.md};
  letter-spacing: 0.02em;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: ${props => props.theme.typography.weights.extrabold};
  margin-bottom: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 1.5vw, 1.125rem);
  color: ${props => props.theme.colors.subtextGray};
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ProcessWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const StepsNav = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 6px;
  }
`;

const StepTab = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: ${props => props.active ? props.theme.gradients.primary : props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.active ? 'transparent' : props.theme.colors.borderSubtle};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${props => props.active ? 'white' : props.theme.colors.textDark};

  &:hover {
    border-color: ${props => props.active ? 'transparent' : props.theme.colors.accentPurple};
    transform: translateY(-2px);
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 10px 16px;
    gap: 8px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 10px 14px;
    flex: 1;
    min-width: calc(50% - 6px);
    justify-content: center;
  }
`;

const StepNumber = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.active ? 'rgba(255,255,255,0.2)' : props.theme.gradients.subtle};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.active ? 'white' : props.theme.colors.accentPurple};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
  }
`;

const StepLabel = styled.span`
  font-size: 0.9375rem;
  font-weight: ${props => props.theme.typography.weights.medium};
  white-space: nowrap;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 0.875rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const ContentArea = styled.div`
  position: relative;
  min-height: 320px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    min-height: auto;
  }
`;

const StepContent = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  align-items: center;
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 24px;
  padding: ${props => props.theme.spacing.xl};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.theme.gradients.primary};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    padding: ${props => props.theme.spacing.lg};
  }
`;

const StepTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const StepTitle = styled.h3`
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.textLight};
  letter-spacing: -0.02em;
  margin: 0;
`;

const StepDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.subtextGray};
  line-height: 1.6;
  margin: 0;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9375rem;
  color: ${props => props.theme.colors.textDark};
`;

const CheckIcon = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${props => props.theme.gradients.subtle};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.accentPurple};
  font-size: 0.75rem;
  flex-shrink: 0;
`;

const IllustrationArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  background: ${props => props.theme.gradients.subtle};
  border-radius: 16px;
  position: relative;
  overflow: hidden;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: 160px;
  }
`;

const StepIcon = styled(motion.div)`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: ${props => props.theme.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: ${props => props.theme.shadows.glow};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 64px;
    height: 64px;
    border-radius: 16px;
  }
`;

const FloatingDot = styled(motion.div)`
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.theme.colors.accentPurple};
  opacity: 0.5;
`;

// Step icons
const DescribeIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AnalyzeIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" strokeLinecap="round" />
  </svg>
);

const TestIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LearnIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const HorizontalScroll = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const steps = [
    {
      number: '1',
      label: 'Learn',
      title: 'Build Lean Foundations',
      description: 'Earn the Lean White Belt across 8 interactive modules. Daily Library cards keep the team sharp between deliverables.',
      features: ['8-module White Belt with shareable credential', 'Respect for People certification', 'Daily flashcards & cheat sheets', 'Waste Hunt and Bottleneck simulation games'],
      icon: LearnIcon,
    },
    {
      number: '2',
      label: 'Plan',
      title: 'Align on What Matters',
      description: 'Hoshin Kanri X-matrices and Charter Pilot project briefs keep breakthrough goals visible from leadership down to the floor.',
      features: ['Hoshin Kanri X-matrix planning', 'Annual objectives, initiatives, and metrics', 'Monthly review tracking', 'Project charters with team collaboration'],
      icon: AnalyzeIcon,
    },
    {
      number: '3',
      label: 'Execute',
      title: 'Run the Work',
      description: 'Job Instructor turns SOPs into TWI sheets with auto-JHA. Audits goes from checklist to insight. Teams keeps everyone in sync.',
      features: ['AI-generated TWI Job Instructions', 'Auto Job Hazard Analysis per step', 'Audit checklists with public response links', 'Team workspaces with leaderboards'],
      icon: TestIcon,
    },
    {
      number: '4',
      label: 'Improve',
      title: 'Coach the Team',
      description: 'NEAL coaches your A3 problem solving. People Compass shows your team\'s DISC mix. Respect for People builds daily habits.',
      features: ['NEAL conversational AI for A3s', 'DISC team profiles via People Compass', 'Daily Respect-for-People practice', 'AI-generated training quizzes'],
      icon: DescribeIcon,
    },
  ];

  const currentStep = steps[currentIndex];
  const IconComponent = currentStep.icon;

  return (
    <ProcessSection id="how-it-works">
      <BackgroundGlow />
      <Container>
        <SectionHeader>
          <SectionLabel
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            How It Works
          </SectionLabel>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Learn. Plan. Execute. Improve.
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Four stages of continuous improvement, supported end-to-end.
          </SectionSubtitle>
        </SectionHeader>

        <ProcessWrapper>
          <StepsNav>
            {steps.map((step, index) => (
              <StepTab
                key={index}
                active={index === currentIndex}
                onClick={() => setCurrentIndex(index)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <StepNumber active={index === currentIndex}>{step.number}</StepNumber>
                <StepLabel>{step.label}</StepLabel>
              </StepTab>
            ))}
          </StepsNav>

          <ContentArea>
            <AnimatePresence mode="wait">
              <StepContent
                key={currentIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <StepTextContent>
                  <StepTitle>{currentStep.title}</StepTitle>
                  <StepDescription>{currentStep.description}</StepDescription>
                  <FeaturesList>
                    {currentStep.features.map((feature, idx) => (
                      <FeatureItem key={idx}>
                        <CheckIcon>✓</CheckIcon>
                        {feature}
                      </FeatureItem>
                    ))}
                  </FeaturesList>
                </StepTextContent>

                <IllustrationArea>
                  <FloatingDot
                    animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
                    transition={{ duration: 4, repeat: Infinity }}
                    style={{ top: '20%', left: '15%' }}
                  />
                  <FloatingDot
                    animate={{ x: [0, -20, 0], y: [0, 25, 0] }}
                    transition={{ duration: 5, repeat: Infinity, delay: 0.5 }}
                    style={{ bottom: '25%', right: '20%' }}
                  />
                  <FloatingDot
                    animate={{ x: [0, 15, 0], y: [0, 15, 0] }}
                    transition={{ duration: 3.5, repeat: Infinity, delay: 1 }}
                    style={{ top: '60%', left: '25%' }}
                  />
                  <StepIcon
                    key={`icon-${currentIndex}`}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  >
                    <IconComponent />
                  </StepIcon>
                </IllustrationArea>
              </StepContent>
            </AnimatePresence>
          </ContentArea>
        </ProcessWrapper>
      </Container>
    </ProcessSection>
  );
};

export default HorizontalScroll;
